import { useState } from 'react';
import { Button } from '@flintmint/web-shared/components/button';
import { Chip } from '@flintmint/web-shared/components/chip';
import { Popover } from '@flintmint/web-shared/components/popover';

import type { FlowWithId } from '@/services/flows-service';

import styles from './flow-chips-cell.module.scss';

interface FlowChipsCellProps {
  flowIds: string[];
  flows: FlowWithId[];
  onChange: (flowIds: string[]) => void | Promise<void>;
}

export const FlowChipsCell = ({ flowIds, flows, onChange }: FlowChipsCellProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const applyChange = async (nextFlowIds: string[]) => {
    setIsSaving(true);
    try {
      await onChange(nextFlowIds);
    } finally {
      setIsSaving(false);
    }
  };

  const flowNameById = new Map(flows.map((flow) => [flow.id, flow.name]));
  const addableFlows = flows.filter((flow) => !flowIds.includes(flow.id));

  return (
    <div className={styles.cell} data-testid="module-flow-chips">
      {flowIds.map((flowId) => (
        <Chip
          key={flowId}
          label={flowNameById.get(flowId) ?? flowId}
          color="secondary"
          onClose={isSaving ? undefined : () => applyChange(flowIds.filter((id) => id !== flowId))}
        />
      ))}
      {addableFlows.length > 0 && (
        <Popover
          placement="bottom-start"
          content={({ close }) => (
            <ul className={styles.addMenu}>
              {addableFlows.map((flow) => (
                <li key={flow.id}>
                  <button
                    type="button"
                    className={styles.addMenuItem}
                    disabled={isSaving}
                    data-testid="module-flow-option"
                    data-flow-name={flow.name}
                    onClick={() => {
                      close();
                      void applyChange([...flowIds, flow.id]);
                    }}
                  >
                    {flow.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        >
          <Button
            size="small"
            appearance="outline"
            color="light"
            iconOnly
            disabled={isSaving}
            aria-label="Add flow"
            data-testid="module-flow-add-btn"
          >
            +
          </Button>
        </Popover>
      )}
    </div>
  );
};
